import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import MarkdownRenderer from '../MarkdownRenderer/MarkdownRenderer';
import useBodyScrollLock from '../../utils/useBodyScrollLock';
import './ComposerPreviewModal.css';

const ComposerPreviewModal = ({
  isOpen = false,
  title = 'Preview',
  content = '',
  previewNode = null,
  author = 'you',
  avatarUrl = '',
  submitLabel = 'Post',
  isSubmitting = false,
  onClose = () => {},
  onKeepWriting = () => {},
  onSubmit = () => {},
  focusTargetRef = null,
}) => {
  useBodyScrollLock(isOpen);

  const focusEditorTarget = useCallback(() => {
    window.setTimeout(() => {
      focusTargetRef?.current?.focus?.();
    }, 0);
  }, [focusTargetRef]);

  const handleClose = useCallback(() => {
    onClose();
    focusEditorTarget();
  }, [onClose, focusEditorTarget]);

  const handleKeepWriting = useCallback(() => {
    onKeepWriting();
    focusEditorTarget();
  }, [onKeepWriting, focusEditorTarget]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onEsc = (event) => {
      if (event.key === 'Escape') handleClose();
    };

    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const safeAuthor = String(author || 'you');
  const safeAvatar = avatarUrl || `https://images.hive.blog/u/${safeAuthor}/avatar`;

  const modalContent = (
    <div className="composer-preview-overlay" onClick={handleClose}>
      <div className="composer-preview-modal" onClick={(event) => event.stopPropagation()}>
        <div className="composer-preview-header">
          <h3>{title}</h3>
          <button type="button" className="composer-preview-close" onClick={handleClose} aria-label="Close preview">
            x
          </button>
        </div>

        <div className="composer-preview-card">
          {previewNode ? (
            <div className="composer-preview-live-card-wrap">
              <div className="composer-preview-live-card-meta">
                <span>Preview</span>
                <span className="post-via-app">via <em>Flexus</em></span>
              </div>
              <div className="composer-preview-live-card">{previewNode}</div>
            </div>
          ) : (
            <>
              <div className="composer-preview-author-row">
                <img
                  src={safeAvatar}
                  alt={safeAuthor}
                  className="composer-preview-avatar"
                  onError={(event) => {
                    event.currentTarget.src = `https://ui-avatars.com/api/?name=${safeAuthor}&background=random`;
                  }}
                />
                <div className="composer-preview-meta">
                  <strong>@{safeAuthor}</strong>
                  <div className="composer-preview-meta-row">
                    <span>Preview</span>
                    <span className="post-via-app" title="flexus/1.0.0">via <em>Flexus</em></span>
                  </div>
                </div>
              </div>
              <div className="composer-preview-body">
                <MarkdownRenderer content={String(content || '').trim()} />
              </div>
            </>
          )}
        </div>

        <div className="composer-preview-actions">
          <button type="button" className="btn-secondary" onClick={handleKeepWriting}>
            Keep writing
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={onSubmit}
            disabled={isSubmitting || !String(content || '').trim()}
          >
            {isSubmitting ? 'Posting...' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

export default ComposerPreviewModal;
